import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'

export type BrandName = 'cleartrip' | 'flyin'

export interface PopularAirportPayload {
    airportId: string
    airportCode: string
    weight: number
    cityId: string
    provinceId: string
    cityName: string
    airportName: string
    countryName: string
    countryCode: string
    countryMisspell: string
    cityMisspell: string
    score: number
}

export interface PopularAirportItem {
    priority: number
    nearby: boolean
    text: string
    score: number
    payload: PopularAirportPayload
}

export interface PopularAirportsResponse {
    en: PopularAirportItem[]
    ar: PopularAirportItem[]
}

// Static JSON hosted per brand.
// - Cleartrip: https://ui.cltp.co/json/popular-airports-ct.json
// - Flyin:    https://ui.cltp.co/json/popular-airports-flyin.json
const getUrl = (brandName: BrandName) => {
    const normalized = (brandName || '').toString().toLowerCase()
    const file = normalized === 'cleartrip' ? 'popular-airports-ct.json' : 'popular-airports-flyin.json'
    return `https://ui.cltp.co/json/${file}`
}

const baseQueryWithRetry = retry(
    fetchBaseQuery({
        baseUrl: '',
        prepareHeaders: headers => {
            headers.set('Accept', 'application/json')
            headers.set('Content-Type', 'application/json')
            headers.set('Cache-Control', 'no-cache')
            return headers
        },
        // Handle malformed JSON responses
        responseHandler: async response => {
            const text = await response.text()
            return text
        }
    }),
    { maxRetries: 2 }
)

export const popularAirportApi = createApi({
    reducerPath: 'popularAirportApi',
    baseQuery: baseQueryWithRetry,
    tagTypes: ['PopularAirports'],
    endpoints: builder => ({
        getPopularAirports: builder.query<PopularAirportsResponse, BrandName>({
            query: brandName => getUrl(brandName),
            providesTags: ['PopularAirports'],
            transformResponse: (response: string) => JSON.parse(response) as PopularAirportsResponse
        })
    })
})

export const { useGetPopularAirportsQuery, useLazyGetPopularAirportsQuery } = popularAirportApi
export default popularAirportApi

